import type { ReactNode } from 'react';
import { formatMoney } from '@/utils/format';
import type {
  LeaveRequestStatus,
  LoanStatus,
  PayrollAdjustmentType,
  PayrollLineSource,
  PayrollPeriodStatus,
  SalaryStatus,
} from '@/types/hr';

/*
 * Pieces every HR screen uses.
 *
 * Kept together so the eleven screens in this module cannot drift into eleven
 * slightly different ways of showing a status or an amount — which is exactly
 * what happened across the first few finance screens before they were pulled
 * into one file.
 */

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'border-line bg-surface text-muted',
  success: 'border-[color:var(--sms-success)] text-[color:var(--sms-success)]',
  warning: 'border-[color:var(--sms-warning)] text-[color:var(--sms-warning)]',
  danger: 'border-[color:var(--sms-danger)] text-[color:var(--sms-danger)]',
  info: 'border-accent text-accent',
};

/** A small status pill. Text as well as colour: colour alone is not a label. */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.03em] ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

const LEAVE_TONE: Record<LeaveRequestStatus, Tone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  MANAGER_APPROVED: 'info',
  HR_APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
  TAKEN: 'success',
};

export function LeaveStatusBadge({ status }: { status: LeaveRequestStatus }) {
  return <Badge tone={LEAVE_TONE[status]}>{humanise(status)}</Badge>;
}

const PAYROLL_TONE: Record<PayrollPeriodStatus, Tone> = {
  DRAFT: 'neutral',
  CALCULATED: 'warning',
  APPROVED: 'info',
  PAID: 'success',
  CANCELLED: 'danger',
};

export function PayrollStatusBadge({ status }: { status: PayrollPeriodStatus }) {
  return <Badge tone={PAYROLL_TONE[status]}>{humanise(status)}</Badge>;
}

const LOAN_TONE: Record<LoanStatus, Tone> = {
  ACTIVE: 'info',
  SUSPENDED: 'warning',
  SETTLED: 'success',
  CANCELLED: 'neutral',
};

export function LoanStatusBadge({ status }: { status: LoanStatus }) {
  return <Badge tone={LOAN_TONE[status]}>{humanise(status)}</Badge>;
}

export function SalaryStatusBadge({ status }: { status: SalaryStatus }) {
  return <Badge tone={status === 'ACTIVE' ? 'success' : 'neutral'}>{humanise(status)}</Badge>;
}

/** Where a payslip line came from, when it is not simply part of the package. */
export function SourceBadge({ source }: { source: PayrollLineSource }) {
  if (source === 'STRUCTURE') {
    return null;
  }
  const tone: Tone = source === 'LOAN_REPAYMENT' || source === 'UNPAID_LEAVE' ? 'warning' : 'info';
  return <Badge tone={tone}>{humanise(source)}</Badge>;
}

export function AdjustmentBadge({ type }: { type: PayrollAdjustmentType }) {
  const earning = type === 'OVERTIME' || type === 'BONUS' || type === 'REIMBURSEMENT';
  return <Badge tone={earning ? 'success' : 'danger'}>{humanise(type)}</Badge>;
}

/**
 * An amount, right-aligned and tabular.
 *
 * <p>Tabular figures matter in a column of money: proportional digits make a
 * list of salaries impossible to scan for the odd one out.
 */
export function Money({ value, bold = false }: { value?: number | null; bold?: boolean }) {
  return (
    <span
      className={`tabular-nums ${bold ? 'font-bold text-ink' : 'text-ink'}`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value === null || value === undefined ? '—' : formatMoney(value)}
    </span>
  );
}

/** A number of days, showing halves but not trailing zeroes. */
export function Days({ value }: { value?: number | null }) {
  if (value === null || value === undefined) {
    return <span className="text-subtle">—</span>;
  }
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return <span className="tabular-nums">{text}</span>;
}

/** A headline figure with a label under it. */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  const accent =
    tone === 'danger'
      ? 'var(--sms-danger)'
      : tone === 'warning'
        ? 'var(--sms-warning)'
        : tone === 'success'
          ? 'var(--sms-success)'
          : 'var(--sms-accent)';

  return (
    <div className="border border-line bg-bg p-4">
      <div className="h-[3px] w-8" style={{ background: accent }} aria-hidden="true" />
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
        {label}
      </p>
      <p className="mt-1 text-[26px] font-extrabold leading-none text-ink">{value}</p>
      {hint !== undefined && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/** A labelled row for a detail panel. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-2 last:border-0">
      <dt className="shrink-0 text-xs uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-ink">{children}</dd>
    </div>
  );
}

/** A horizontal bar for a breakdown, sized against the largest value shown. */
export function BarRow({
  label,
  value,
  max,
  suffix,
}: {
  label: string;
  value: number;
  max: number;
  suffix?: ReactNode;
}) {
  const width = max <= 0 ? 0 : Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-ink">{label}</span>
        <span className="shrink-0 tabular-nums text-muted">{suffix ?? value}</span>
      </div>
      <div className="mt-1 h-1.5 w-full bg-surface">
        <div className="h-full bg-accent" style={{ width: `${width}%` }} aria-hidden="true" />
      </div>
    </div>
  );
}

/** ACADEMIC_COORDINATOR → "academic coordinator". */
export function humanise(value: string): string {
  return value.replaceAll('_', ' ').toLowerCase();
}

/** The leave year a date falls in. Leave is budgeted by calendar year. */
export function leaveYearOf(date: string | Date = new Date()): number {
  return typeof date === 'string' ? Number(date.slice(0, 4)) : date.getFullYear();
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The first day of the current month, for a payroll period's default range. */
export function startOfMonth(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

/** The last day of the current month. */
export function endOfMonth(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
}
